import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';
import { useCallback, useMemo } from 'react';
import { getFiltersStateParser } from '@/lib/parsers';
import type { PurchasePlanning } from '@/types/core/purchase-planning/purchase-planning';
import type { ExtendedColumnFilter } from '@/types/data-table';

function createSupplierFilter(
  supplierIds: string[],
): ExtendedColumnFilter<PurchasePlanning> {
  return {
    id: 'supplierId',
    value: supplierIds,
    variant: 'multiSelect',
    operator: 'inArray',
    filterId: 'purchase-planning-supplier-filter',
  };
}

export function usePurchasePlanningFilters() {
  const [filters, setFilters] = useQueryState(
    'filters',
    getFiltersStateParser<PurchasePlanning>().withDefault([]),
  );
  const [supplierIds, setSupplierIds] = useQueryState(
    'supplierIds',
    parseAsArrayOf(parseAsString, ',').withDefault([]),
  );

  const filtersWithSupplier = useMemo(() => {
    const filtersWithoutSupplier = filters.filter(
      (filter) => filter.id !== 'supplierId',
    );

    if (supplierIds.length === 0) {
      return filtersWithoutSupplier;
    }

    return [...filtersWithoutSupplier, createSupplierFilter(supplierIds)];
  }, [filters, supplierIds]);

  const setSelectedSupplierIds = useCallback(
    (nextSupplierIds: string[] | undefined) => {
      setSupplierIds(nextSupplierIds ?? []);
    },
    [setSupplierIds],
  );

  return {
    filters,
    setFilters,
    supplierIds,
    setSelectedSupplierIds,
    filtersWithSupplier,
  };
}
